import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Alert,
  useWindowDimensions,
  Platform,
  
} from "react-native";
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { clearToastNotification } from '../../../store/slices/actionPlanSlice';
import { refetchNotifications } from '../../../store/slices/notificationSlice';
import { useToast } from '../../../components/LhToastContainer';
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import CrossPlatformDropdown from "../../../components/CrossPlatformDropdown";
import LhDatePicker from "../../../components/DatePicker/LhDatePicker";
import LhTextArea from "../../../components/LhTextArea";
import LhTextInput from "../../../components/LhTextInput";
import { CreateActionPlanFormProps } from '../../ActionPlan/types';
import { useCreateActionPlanForm } from '../../ActionPlan/hooks/useCreateActionPlanForm';
import { mapApiSourceToOptionId, getSourceDisplayText, getTypeDisplayText } from '../../ActionPlan/utils/formOptions';
import { createActionPlanFormStyles } from '../../ActionPlan/styles/createActionPlanForm.styles';
import { TopOpportunityItem } from "./TopOpportunitiesTable";

interface StandUpCreateProps extends CreateActionPlanFormProps {
  topOpportunity?: boolean;
  onTopOpportunityChange?: (v: boolean) => void;
  actionPlanData?: TopOpportunityItem;
  standupSessionId?: string;
  standupId?: string;
  isEditMode?: boolean;
  isContinuousImprovement?: boolean;
  isLighthouseExecution?: boolean;
  isSparcKpi?: boolean;
}

const CreateStandUpActionPlanForm: React.FC<StandUpCreateProps> = ({
  data = {},
  type = "create",
  onSubmit,
  onCancel,
  onFormSubmitReady,
  onViewAuditTrail,
  isMobile = false,
  topOpportunity: controlledTopOpportunity,
  onTopOpportunityChange,
  actionPlanData,
  standupSessionId,
  standupId,
  isEditMode,
  isContinuousImprovement,
  isLighthouseExecution,
  isSparcKpi,
}) => {
  const { width: screenWidth } = useWindowDimensions();
  const isTablet = screenWidth >= 768;


  const initialCapabilities = actionPlanData?.capabilities || [];
  const area = actionPlanData?.AREA || actionPlanData?.capabilities?.[0]?.question_title || null;
  const typeValue = actionPlanData?.TYPE || null; // Track pre-populated Type from Lighthouse cards
  const sourceName = actionPlanData?.SOURCE ? getSourceDisplayText(actionPlanData.SOURCE) : null;

  // Debug logging
  React.useEffect(() => {
    if (sourceName || actionPlanData) {
      console.log('[CreateStandUpActionPlan] Initializing with data:');
      console.log('  - sourceName:', sourceName);
      console.log('  - actionPlanData:', actionPlanData);
      console.log('  - actionPlanData.SOURCE:', actionPlanData?.SOURCE);
      console.log('  - actionPlanData.TYPE:', actionPlanData?.TYPE);
      console.log('  - actionPlanData.AREA:', actionPlanData?.AREA);
      console.log('  - area variable:', area);
    }
  }, [sourceName, actionPlanData, area]);

  // Merge actionPlanData into data for the hook
  // IMPORTANT: Separate logic for Lighthouse Execution vs Lighthouse Tasks
  // For EDIT/VIEW mode, use the full data prop to preserve existing action plan data
  const mergedData = React.useMemo(() => {
    // For edit/view mode, use the original data prop as-is
    if (type === 'edit' || type === 'view') {
      console.log('[CreateStandUpActionPlan] Edit/View mode - using full data prop:', data);
      return data;
    }
    
    // For create mode with Lighthouse Execution - separate logic
    if (type === 'create' && isLighthouseExecution && actionPlanData) {
      // Parse dates without timezone conversion to prevent date shifting
      const parseUTCDate = (isoDateString: string | null | undefined): Date | null => {
        if (!isoDateString) return null;
        try {
          const parts = isoDateString.split('T')[0].split('-');
          if (parts.length !== 3) return null;
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const day = parseInt(parts[2], 10);
          return new Date(year, month, day);
        } catch (e) {
          return null;
        }
      };
      
      const startDate = parseUTCDate(actionPlanData.START_DATE);
      const targetDate = parseUTCDate(actionPlanData.TARGET_DATE);
      
      const merged: any = {
        startDate,
        targetDate,
      };
      
      // Source: "Lighthouse Execution" (disabled)
      if (actionPlanData.SOURCE) {
        merged.source = { displayText: actionPlanData.SOURCE, id: actionPlanData.SOURCE.toLowerCase(), value: actionPlanData.SOURCE };
        merged.isSourceDisabled = true;
      }
      
      if (actionPlanData.TYPE) {
        const formattedType = getTypeDisplayText(actionPlanData.TYPE);
        merged.type = { displayText: formattedType, id: actionPlanData.TYPE.toLowerCase(), value: actionPlanData.TYPE.toLowerCase() };
      }
      
      // Area: Card title (disabled)
      if (actionPlanData.AREA) {
        merged.area = actionPlanData.AREA;
        merged.isAreaDisabled = true;
      }
      
      console.log('[CreateStandUpActionPlan] Create mode - Lighthouse Execution:', merged);
      console.log('  - Source (disabled):', merged.source);
      console.log('  - Type (editable):', merged.type);
      console.log('  - Area (disabled):', merged.area);
      return merged;
    }
    
    // For create mode with lighthouse task data (existing logic - unchanged)
    if (type === 'create' && (actionPlanData?.START_DATE || actionPlanData?.TARGET_DATE || actionPlanData?.TYPE || actionPlanData?.AREA || actionPlanData?.SOURCE)) {
      // Parse dates without timezone conversion to prevent date shifting
      const parseUTCDate = (isoDateString: string | null | undefined): Date | null => {
        if (!isoDateString) return null;
        try {
          const parts = isoDateString.split('T')[0].split('-');
          if (parts.length !== 3) return null;
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const day = parseInt(parts[2], 10);
          return new Date(year, month, day);
        } catch (e) {
          return null;
        }
      };
      
      const startDate = parseUTCDate(actionPlanData.START_DATE);
      const targetDate = parseUTCDate(actionPlanData.TARGET_DATE);
      
      const merged: any = {
        startDate, // Pre-populated start date from standup session
        targetDate, // Pre-populated target date from standup session
      };
      
      // Pre-populate Source with Lighthouse container name and mark it as disabled
      if (actionPlanData.SOURCE) {
        merged.source = { displayText: actionPlanData.SOURCE, id: actionPlanData.SOURCE.toLowerCase(), value: actionPlanData.SOURCE };
        merged.isSourceDisabled = true; // Flag to disable the source field
      }
      
      // Pre-select Type dropdown with KFA for lighthouse task action plans or KPI for SPARC cards (editable)
      if (actionPlanData.TYPE) {
        const formattedType = getTypeDisplayText(actionPlanData.TYPE);
        merged.type = { displayText: formattedType, id: actionPlanData.TYPE.toLowerCase(), value: actionPlanData.TYPE.toLowerCase() };
      }
      
      // Pre-fill Area with lighthouse card title and mark it as disabled
      if (actionPlanData.AREA) {
        merged.area = actionPlanData.AREA;
        merged.isAreaDisabled = true; // Flag to disable the area field
      }
      
      console.log('[CreateStandUpActionPlan] Create mode with lighthouse card - merged data:', merged);
      console.log('  - Start Date:', merged.startDate);
      console.log('  - Target Date:', merged.targetDate);
      console.log('  - Source:', merged.source);
      console.log('  - Type:', merged.type);
      console.log('  - Area:', merged.area);
      console.log('  - isSourceDisabled:', merged.isSourceDisabled);
      console.log('  - isAreaDisabled:', merged.isAreaDisabled);
      return merged;
    }
    
    // For create mode without lighthouse card, start fresh
    return {};
  }, [type, actionPlanData?.START_DATE, actionPlanData?.TARGET_DATE, actionPlanData?.TYPE, actionPlanData?.AREA, actionPlanData?.SOURCE, data]);

  // Initialize isTopOpportunity state before using it in hook options
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const initialTop = data?.topOpportunity;
  const [isTopOpportunity, setIsTopOpportunity] = useState<boolean>(controlledTopOpportunity ?? initialTop ?? true);
  
  // Tooltip state for Area field when missing in edit mode
  const [showAreaTooltip, setShowAreaTooltip] = useState(false);

  const {
    title,
    setTitle,
    selectedSource,
    setSelectedSource,
    selectedType,
    setSelectedType,
    selectedPriority,
    setSelectedPriority,
    selectedOwner,
    selectedAssignee,
    setSelectedAssignee,
    startDate,
    setStartDate,
    targetDate,
    handleTargetDateChange,
    actionPlan,
    setActionPlan,
    notes,
    setNotes,
    frontlineInvolvement,
    setFrontlineInvolvement,
    selectedEmployee,
    setSelectedEmployee,
    selectedArea,
    setSelectedArea,
    areaOptions,
    isView,
    loading,
    dateValidationError,
    submissionError,
    validationErrors,
    setSubmissionError,
    assigneeLoading,
    employeeLoading,
    isCreateDisabled,
    sourceOptions,
    typeOptions,
    priorityOptions,
    ownerOption,
    assigneeList,
    employeeList,
    handleSubmit,
    formatDisplayDate,
  } = useCreateActionPlanForm(mergedData, type, onSubmit, { 
    sendAreaInPayload: true, 
    area,
    standupSessionId,
    standupId,
    isEditMode,
    isTopOpportunity: isTopOpportunity,
    capabilities: initialCapabilities,
    isContinuousImprovement,
    isLighthouseExecution,
    isSparcKpi,
  });

  // Debug log to check if selectedType was initialized correctly
  React.useEffect(() => {
    console.log('[CreateStandUpActionPlan] Component render/update:');
    console.log('  - type prop:', type);
    console.log('  - isView:', isView);
    console.log('  - selectedSource:', selectedSource);
    console.log('  - selectedType:', selectedType);
  }, [selectedSource, selectedType, type, isView]);

  // Keep local state in sync when parent controls the value
  React.useEffect(() => {
    if (controlledTopOpportunity !== undefined) {
      setIsTopOpportunity(controlledTopOpportunity);
    }
  }, [controlledTopOpportunity]);

  // Keep a ref to the latest handleSubmit so we can provide a stable callback to parent
  const handleSubmitRef = React.useRef(handleSubmit);
  React.useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Expose the handleSubmit function to parent via callback
  // This allows the parent (ActionPlanDetails) to trigger form submission from Save button
  React.useEffect(() => {
    if (onFormSubmitReady && type === 'edit') {
      // Provide a stable wrapper that calls the latest handleSubmit via ref
      const stableSubmit = () => {
        const fn = handleSubmitRef.current;
        if (fn) {
          return fn();
        }
        return Promise.resolve(null as any);
      };
      // Debug log to help trace registration
      // eslint-disable-next-line no-console
      console.log('[CreateStandUpActionPlan] registering form submit callback, type=', type);
      onFormSubmitReady(stableSubmit);
    }
    // Intentionally exclude handleSubmit from deps so this effect doesn't run every render
  }, [onFormSubmitReady, type]);

  // Helper to display read-only pairs matching Figma: small label + medium content
  const ReadOnlyItem: React.FC<{ label: string; content?: string | null; multiline?: boolean }> = ({ label, content, multiline }) => (
    <View style={createActionPlanFormStyles.readOnlyItem}>
      <Text style={createActionPlanFormStyles.viewLabelText}>{label}</Text>
      <Text style={[createActionPlanFormStyles.viewContentText, multiline && createActionPlanFormStyles.viewContentMultiline]}>{content ?? '--'}</Text>
    </View>
  );

  // Custom toggle to match design
  const ToggleSwitch: React.FC<{ value: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ value, onChange, disabled = false }) => (
    <TouchableOpacity
      activeOpacity={disabled ? 1 : 0.9}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      onPress={disabled ? undefined : () => onChange(!value)}
      style={{
        width: 56,
        height: 28,
        borderRadius: 16,
        backgroundColor: value ? '#034895' : '#E0E0E0',
        padding: 2,
        justifyContent: 'center',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: value ? 'flex-end' : 'flex-start' }}>
        <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: '#FFFFFF' }} />
      </View>
    </TouchableOpacity>
  );

 const formatDate = (d: Date | null) => {
    if (!d) return '--';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return String(d);
    }
  };

  // Helper to detect if selectedSource represents 'General'
  const isSourceGeneral = React.useMemo(() => {
    if (!selectedSource) return false;
    const v = (selectedSource as any).value ?? (selectedSource as any).displayText ?? '';
    return String(v).toLowerCase() === 'general';
  }, [selectedSource]);

  // Detect if source came from lighthouse task (should be disabled in create mode)
  const isSourceFromLighthouseTask = React.useMemo(() => {
    return type === 'create' && !!actionPlanData?.SOURCE;
  }, [type, actionPlanData?.SOURCE]);

  // Handle source change - clear type when source changes
  const handleSourceChange = (val: any) => {
    const newSource = val;
    const prevIsGeneral = selectedSource ? String((selectedSource as any).value ?? (selectedSource as any).displayText ?? '').toLowerCase() === 'general' : false;
    const newIsGeneral = newSource ? String((newSource as any).value ?? (newSource as any).displayText ?? '').toLowerCase() === 'general' : false;
    
    // Clear type when switching between General and non-General sources
    if (prevIsGeneral !== newIsGeneral) {
      setSelectedType(null);
    }
    
    setSelectedSource(newSource);
  };

  const handleToggleTopOpportunity = () => {
    const next = !isTopOpportunity;
    setIsTopOpportunity(next);
    if (onTopOpportunityChange) {
      try { onTopOpportunityChange(next); } catch (e) { /* ignore */ }
    }
  };
  return (
    <View style={createActionPlanFormStyles.root}>
      {/* Error Banner */}
      {submissionError && (
        <View style={createActionPlanFormStyles.errorBanner}>
          <View style={createActionPlanFormStyles.errorBannerContent}>
            <MaterialIcons name="error" size={20} color="#D32F2F" />
            <View style={createActionPlanFormStyles.errorTextContainer}>
              <Text style={createActionPlanFormStyles.errorTitle}>{submissionError.title}</Text>
              <Text style={createActionPlanFormStyles.errorMessage}>{submissionError.message}</Text>
            </View>
          </View>
          <TouchableOpacity 
            onPress={() => setSubmissionError(null)}
            style={createActionPlanFormStyles.errorCloseButton}
          >
            <MaterialIcons name="close" size={20} color="#D32F2F" />
          </TouchableOpacity>
        </View>
      )}
      
      <View style={createActionPlanFormStyles.card}>
        {/* Header: Title + Top Opportunity toggle (matches Figma screenshot) */}
        {/* iOS mobile: stack title and toggle vertically; web/iPad keep them on the same row */}
        {(() => {
          const isIOSMobile = Platform.OS === 'ios' && !isTablet;
          return (
            <View style={{ flexDirection: isIOSMobile ? 'column' : 'row', alignItems: isIOSMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: isIOSMobile ? undefined : 1 }}>
                <View style={{ width: 34, height: 34, borderRadius: 6, borderWidth: 1, borderColor: '#E6EEF8', backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
                  <MaterialIcons name="edit" size={18} color="#034895" />
                </View>
                {isView ? (
                  <Text style={{ fontSize: 28, fontWeight: '700', color: '#1E2A32', lineHeight: 40 }}>{title || '--'}</Text>
                ) : (
                  <LhTextInput
                    placeholder="Title"
                    value={title}
                    onChangeText={setTitle}
                    editable={!isView}
                    placeholderTextColor={'#6c757d'}
                    style={{ flex: 1, fontSize: 28, fontWeight: '700', color: '#1E2A32', lineHeight: 40 }}
                  />
                )}
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: isIOSMobile ? 8 : 0 }}>
                <Text style={{ fontSize: 12, color: '#6B6B6B', marginRight: 8 }}>Top Opportunity</Text>
                <ToggleSwitch value={isTopOpportunity} onChange={handleToggleTopOpportunity} disabled={isView} />
              </View>
            </View>
          );
        })()}
        {/* Row 1: Title, Source */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Source" content={sourceName ?? selectedSource?.displayText ?? '--'} />
            ) : isSourceFromLighthouseTask ? (
              <>
                <Label text="Source" required showInfo />
                <CrossPlatformDropdown
                  childList={sourceOptions}
                  label={''}
                  labelStyle={{ fontWeight: "400",fontSize:12, marginTop:6, marginBottom:6 }}
                  placeholder="Select source"
                  placeholderStyle={{ fontSize:14 }}
                  selection="single"
                  setSelectedValue={handleSourceChange}
                  selectedValue={selectedSource}
                  isDisabled={true}
                  readOnly={true}
                  disableSorting={true}
                  disabledBackgroundColor="#DADADA"
                />
              </>
            ) : (
              <>
                <Label text="Source" required showInfo />
                <CrossPlatformDropdown
                  childList={sourceOptions}
                  label={''}
                  labelStyle={{ fontWeight: "400",fontSize:12, marginTop:6, marginBottom:6 }}
                  placeholder="Select source"
                  placeholderStyle={{ fontSize:14 }}
                  selection="single"
                  setSelectedValue={handleSourceChange}
                  selectedValue={selectedSource}
                  isDisabled={isView}
                  readOnly={isView}
                  disableSorting={true}
                />
              </>
            )}
            {validationErrors?.source && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.source}</Text>}
          </View>
           <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Type" content={selectedType?.displayText ?? (selectedType ?? '--')} />
            ) : (
              <>
                <Label text="Type" required showInfo />
                {/* If Source is 'General' show a free-text input for Type, otherwise show the dropdown */}
                {isSourceGeneral ? (
                  <LhTextInput
                    placeholder="Enter Type"
                    value={typeof selectedType === 'string' ? selectedType : (selectedType?.displayText ?? '')}
                    onChangeText={(val) => setSelectedType(val as any)}
                    editable={!isView}
                    placeholderTextColor={'#6c757d'}
                  />
                ) : (
                  <CrossPlatformDropdown
                    childList={typeOptions}
                    label={''}
                    labelStyle={{ fontWeight: "400",fontSize:12 }}
                    placeholder="Action Type"
                    placeholderStyle={{ fontSize:14 }}
                    selection="single"
                    setSelectedValue={(val) => setSelectedType(val as any)}
                    selectedValue={selectedType}
                    isDisabled={isView}
                    readOnly={isView}
                    disableSorting={true}
                  />
                )}
              </>
            )}
            {validationErrors?.type && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.type}</Text>}
          </View>
        </View>

        {/* Row 2: Area, Priority */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              // Show read-only area when viewing
              <ReadOnlyItem label="Area" content={area?.displayText ?? selectedArea?.displayText ?? (area ?? selectedArea ?? '--')} />
            ) : (
              <>
                <Label text="Area" showInfo />
                <View style={{ position: 'relative' }}>
                  <View
                    style={!!area || type === 'edit' ? { overflow: 'hidden', position: 'relative' } : {}}
                    {...(Platform.OS === 'web' && type === 'edit' && !area && !selectedArea
                      ? {
                          onMouseEnter: () => setShowAreaTooltip(true),
                          onMouseLeave: () => setShowAreaTooltip(false),
                        }
                      : {})}
                  >
                    <CrossPlatformDropdown
                      childList={areaOptions}
                      label={''}
                      labelStyle={{ fontWeight: "400",fontSize:12 }}
                      placeholder="Select Area"
                      placeholderStyle={{ fontSize:14 }}
                      selection="single"
                      setSelectedValue={(val) => setSelectedArea(val as any)}
                      selectedValue={area ? { displayText: area, value: area, id: area } : selectedArea}
                      isDisabled={type === 'edit' || !!area}
                      readOnly={type === 'edit' || !!area}
                      disableSorting={true}
                      style={!!area || type === 'edit' ? { marginRight: -40 } : {}}
                      disabledBackgroundColor="#DADADA"
                    />
                  </View>
                  {showAreaTooltip && type === 'edit' && !area && !selectedArea && Platform.OS === 'web' && (
                    <View style={createActionPlanFormStyles.areaTooltip}>
                      <Text style={createActionPlanFormStyles.areaTooltipText}>Area data is not present</Text>
                    </View>
                  )}
                </View>
              </>
            )}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Priority" content={selectedPriority?.displayText ?? (selectedPriority ?? '--')} />
            ) : (
              <>
                <Label text="Priority" required showInfo />
                <CrossPlatformDropdown
                  childList={priorityOptions}
                  label={''}
                  labelStyle={{ fontWeight: "400",fontSize:12 }}
                  placeholder="Select Priority"
                  placeholderStyle={{ fontSize:14 }}
                  selection="single"
                  setSelectedValue={(val) => setSelectedPriority(val as any)}
                  selectedValue={selectedPriority}
                  isDisabled={isView}
                  readOnly={isView}
                  disableSorting={true}
                />
              </>
            )}
            {validationErrors?.priority && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.priority}</Text>}
          </View>
        </View>

        {/* Row 3: Action Plan Owner, Assignee */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Action Plan Owner" content={selectedOwner?.displayText ?? '--'} />
            ) : (
              <CrossPlatformDropdown
                childList={ownerOption}
                label="Action Plan Owner"
                labelStyle={{ fontWeight: '400', fontSize: 12 }}
                placeholder={'--'}
                placeholderStyle={{ fontSize: 14 }}
                selection="single"
                // Readonly dropdown: for create show the current auth user, for edit show the action plan's owner
                setSelectedValue={() => { /* noop - readonly */ }}
                selectedValue={type === 'create' ? ownerOption?.[0] : (selectedOwner ?? ownerOption?.[0])}
                isDisabled={true}
                readOnly={true}
                disabledBackgroundColor="#DADADA"
              />
            )}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Assignee" content={selectedAssignee?.displayText ?? '--'} />
            ) : (
              <>
                <Label text="Assignee" required showInfo />
                <CrossPlatformDropdown
                  childList={assigneeList}
                  label={''}
                  labelStyle={{ fontWeight: '400', fontSize: 12 }}
                  placeholder={
                    assigneeLoading 
                      ? 'Loading...' 
                      : selectedAssignee?.displayText ?? 'Select Assignee'
                  }
                  placeholderStyle={{ fontSize: 14 }}
                  selection="single"
                  setSelectedValue={(val) => setSelectedAssignee(val as any)}
                  selectedValue={selectedAssignee}
                  isDisabled={isView}
                  readOnly={isView}
                />
              </>
            )}
            {(validationErrors?.assignee || validationErrors?.assignee_gpid || validationErrors?.assignee_email || validationErrors?.assignee_role) && (
              <Text style={createActionPlanFormStyles.errorText}>
                {validationErrors.assignee ?? validationErrors.assignee_gpid ?? validationErrors.assignee_email ?? validationErrors.assignee_role}
              </Text>
            )}
          </View>
        </View>

        {/* Row 4: Start Date, Target Date */}
        <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile, isView && (!isTablet ? createActionPlanFormStyles.rowWrapViewMobile : createActionPlanFormStyles.rowWrapView)]}>
          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Start Date" content={formatDate(startDate)} />
            ) : (
              <>
                <Label text="Start Date" required />
                <LhDatePicker label={''} value={startDate} onChange={setStartDate} placeholder="MM/DD/YYYY" disabled={isView} labelStyle={{fontSize:12}} colorScheme="light" iconColor="#3A3A3A" />
              </>
            )}
            {validationErrors?.start_date && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.start_date}</Text>}
          </View>

          <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
            {isView ? (
              <ReadOnlyItem label="Target Date" content={formatDate(targetDate)} />
            ) : (
              <>
                <Label text="Target Date" required />
                <LhDatePicker label={''} value={targetDate} onChange={handleTargetDateChange} placeholder="MM/DD/YYYY" disabled={isView} labelStyle={{fontSize:12}} colorScheme="light" iconColor="#3A3A3A" />
                {dateValidationError && <Text style={createActionPlanFormStyles.errorText}>{dateValidationError}</Text>}
              </>
            )}
            {validationErrors?.target_date && <Text style={createActionPlanFormStyles.errorText}>{validationErrors.target_date}</Text>}
          </View>
        </View>

        {isView ? (
          // stacked column for view mode (Action Plan and Notes one under another)
          <View style={createActionPlanFormStyles.columnWrap}>
            <View style={createActionPlanFormStyles.fieldColumnFull}>
              <ReadOnlyItem label="Action Plan" content={actionPlan || '--'} multiline />
            </View>

            <View style={createActionPlanFormStyles.fieldColumnFull}>
              <ReadOnlyItem label="Notes" content={notes || '--'} multiline />
            </View>
          </View>
        ) : (
          // original side-by-side layout for create/edit
          <View style={[createActionPlanFormStyles.rowWrap, !isTablet && createActionPlanFormStyles.rowWrapMobile]}>
            <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
              <Label text="Action Plan" required />
              <LhTextArea placeholder="Type here" value={actionPlan} onChangeText={setActionPlan} editable={!isView} style={!isView ? undefined : createActionPlanFormStyles.inputDisabled} />
            </View>

            <View style={isTablet ? createActionPlanFormStyles.fieldColumn : createActionPlanFormStyles.fieldColumnMobile}>
              <Label text="Notes" />
              <LhTextArea placeholder="Type here" value={notes} onChangeText={setNotes} editable={!isView} style={!isView ? undefined : createActionPlanFormStyles.inputDisabled} />
            </View>
          </View>
        )}

        <View style={[createActionPlanFormStyles.checkboxRow, !isTablet && createActionPlanFormStyles.checkboxRowMobile]}>
          {!isView ? (
            <TouchableOpacity
              onPress={() => !isView && setFrontlineInvolvement((v: boolean) => !v)}
              style={createActionPlanFormStyles.checkboxTouch}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: frontlineInvolvement }}
            >
              <MaterialIcons name={frontlineInvolvement ? "check-box" : "check-box-outline-blank"} size={20} color={frontlineInvolvement ? "#034895" : "#6B6B6B"} />
            </TouchableOpacity>
          ) : null}

          <Text style={createActionPlanFormStyles.checkboxLabel}>{isView ? (frontlineInvolvement ? 'Frontline Employee involvement: Yes' : 'Frontline Employee involvement: No') : 'Frontline Employee involvement'}</Text>
        </View>

        {frontlineInvolvement && (
          <View style={createActionPlanFormStyles.employeeSection}>
            {isView ? (
              <ReadOnlyItem label="Employee Name" content={selectedEmployee?.displayText ?? '--'} />
            ) : (
              <>
                <Label text="Employee Name" required showInfo />
                <CrossPlatformDropdown
                  childList={employeeList}
                  label={''}
                  labelStyle={{ fontWeight: "400",fontSize:12 }}
                  placeholder={employeeLoading ? 'Loading...' : 'Select Employee'}
                  placeholderStyle={{ fontSize:14 }}
                  selection="single"
                  setSelectedValue={(val) => setSelectedEmployee(val as any)}
                  selectedValue={selectedEmployee}
                  isDisabled={isView}
                  readOnly={isView}
                  placement="top"
                />
                {/* Inline employee validation: prefer friendly "employee" key (Employee Name), fall back to employee_gpid/employee_name from backend */}
                {(!isView && (validationErrors?.employee || validationErrors?.employee_gpid || validationErrors?.employee_name)) && (
                  <Text style={createActionPlanFormStyles.errorText}>
                    {validationErrors.employee ?? validationErrors.employee_gpid ?? validationErrors.employee_name}
                  </Text>
                )}
              </>
            )}
          </View>
        )}

        {/* View Audit Trail Button - Only for Mobile in View Mode */}
        {isMobile && isView && onViewAuditTrail && (
          <TouchableOpacity 
            onPress={onViewAuditTrail}
            style={createActionPlanFormStyles.viewAuditTrailButton}
            accessibilityLabel="View Audit Trail"
          >
            <Text style={createActionPlanFormStyles.viewAuditTrailButtonText}>View Audit Trail</Text>
          </TouchableOpacity>
        )}

        {type === 'create' && (
          <View style={[createActionPlanFormStyles.actionsRow, !isTablet && createActionPlanFormStyles.actionsRowMobile, isTablet && createActionPlanFormStyles.actionsRowWeb, { marginTop: frontlineInvolvement ? 40 : 10 }]}>
            <TouchableOpacity
              style={[ createActionPlanFormStyles.tertiaryButton]}
              onPress={() => {
                setSubmissionError(null);
                // Clear any global toast validation notification so it doesn't persist after navigation
                dispatch(clearToastNotification());
                onCancel?.();
              }}
            >
              <Text style={createActionPlanFormStyles.tertiaryText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[createActionPlanFormStyles.button, (isCreateDisabled || loading) ? createActionPlanFormStyles.disabledButton : createActionPlanFormStyles.primaryButton]}
              disabled={isCreateDisabled || loading}
              onPress={async () => {
                try {
                  const res = await handleSubmit();
                  if (res?.success) {
                    toast.showToast({
                      header: '',
                      subtext: 'Action plan has been created',
                      variant: 'success',
                    });
                  }
                } finally {
                  try {
                    dispatch(refetchNotifications());
                  } catch (e) {
                    console.warn('Failed to trigger notifications refetch after create:', e);
                  }
                }
              }}
            >
              <View style={createActionPlanFormStyles.primaryContent}>
                {/* <MaterialIcons name="save_alt" size={16} color={isCreateDisabled ? '#6b6b6b' : '#FFFFFF'} style={{marginRight:8}} /> */}
                <Text style={[createActionPlanFormStyles.primaryText, isCreateDisabled && createActionPlanFormStyles.disabledText]}> {loading ? 'Submitting...' : 'Save and Continue'}</Text>
              </View>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
};

const Label: React.FC<{ text: string; required?: boolean; showInfo?: boolean }> = ({ text, required, showInfo = true }) => (
  <View style={[createActionPlanFormStyles.labelRow, { justifyContent: 'space-between' }]}> 
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Text style={createActionPlanFormStyles.labelText}>{text}</Text>
      {showInfo ? <MaterialIcons name="info-outline" size={14} color="#6B6B6B" style={{ marginLeft: 6 }} /> : null}
    </View>
    {required ? <Text style={createActionPlanFormStyles.required}>*Required</Text> : null}
  </View>
);

export default CreateStandUpActionPlanForm;

